import type { RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

interface ScrollState {
  showLeftShadow: boolean;
  showRightShadow: boolean;
}

interface UseScrollShadowsProps {
  scrollContainerRef: RefObject<HTMLElement | null>;
}

const SCROLL_THRESHOLD = 1;

export const useScrollShadows = ({ scrollContainerRef }: UseScrollShadowsProps): ScrollState => {
  const [scrollState, setScrollState] = useState<ScrollState>({
    showLeftShadow: false,
    showRightShadow: false
  });

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;

    if (!container) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const maxScrollLeft = scrollWidth - clientWidth;

    setScrollState({
      showLeftShadow: scrollLeft > SCROLL_THRESHOLD,
      showRightShadow: scrollLeft < maxScrollLeft - SCROLL_THRESHOLD
    });
  }, [scrollContainerRef]);

  // Synchronous initial check to avoid flash
  useLayoutEffect(() => {
    updateScrollState();
  }, [updateScrollState]);

  useEffect(() => {
    const container = scrollContainerRef.current;

    if (!container) {
      return;
    }

    // Throttle scroll updates with requestAnimationFrame
    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          updateScrollState();
          rafId = null;
        });
      }
    };

    // Watch for size changes on container and its children
    const resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });

    container.addEventListener('scroll', handleScroll, { passive: true });
    resizeObserver.observe(container);

    // Also observe the first child (the table) for size changes
    const table = container.querySelector?.('table');

    if (table) {
      resizeObserver.observe(table);
    }

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [scrollContainerRef, updateScrollState]);

  return scrollState;
};
